import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import axios from "axios";
import SiteFooter from "../components/SiteFooter";
import { useLang } from "../i18n/LanguageContext";

const Global = createGlobalStyle`
  @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard.css');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #f0ebe3; font-family: 'Pretendard', sans-serif; }
`;

const spin = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;
const fadeUp = keyframes`from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}`;

const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 440px;
  background: #fff;
  border: 1.5px solid #e0d5c8;
  border-radius: 20px;
  padding: 48px 28px;
  box-shadow: 0 4px 24px rgba(100,70,40,0.10);
  text-align: center;
  animation: ${fadeUp} 0.4s ease;
`;

const Spinner = styled.div`
  width: 48px; height: 48px;
  border: 4px solid #f0e8de;
  border-top-color: #c09a6b;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin: 0 auto 20px;
`;

const Icon = styled.div`font-size: 3rem; margin-bottom: 12px;`;
const Title = styled.h2`font-size: 1.2rem; color: #3d2b1f; font-weight: 800; margin-bottom: 8px;`;
const Desc = styled.p`font-size: 0.85rem; color: #7a6654; line-height: 1.7;`;
const ErrBtn = styled.button`
  margin-top: 20px;
  padding: 12px 28px;
  background: #c09a6b;
  color: #fff;
  border: none;
  border-radius: 24px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
`;

const API = process.env.REACT_APP_API_URL || "";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const [status, setStatus] = useState<"loading" | "ok" | "error">("loading");
  const [errMsg, setErrMsg] = useState("");
  const [cid, setCid] = useState(0);   // 결제까지는 끝난 접수번호 — 실패 화면에서 다시 시도할 때 쓴다

  useEffect(() => {
    // 휴대폰 결제 복귀 지점 — 포트원이 imp_uid·merchant_uid·imp_success를 붙여서 보낸다
    const impUid      = params.get("imp_uid") || "";
    const merchantUid = params.get("merchant_uid") || "";
    const success     = params.get("imp_success") ?? params.get("success");
    // merchant_uid 형식: saju_{상담번호}_{시각}
    const consultationId = Number((merchantUid.split("_")[1] || "0"));

    if (success === "false" || success === "0") {
      setStatus("error");
      setErrMsg(params.get("error_msg") || t("pay.cancelled"));
      return;
    }
    if (!impUid || !consultationId) {
      setStatus("error");
      setErrMsg(t("pay.badInfo"));
      return;
    }

    /* 결제는 이미 끝난 시점이다 — 여기서 한 번 실패했다고 끝내면
       돈만 빠져나간 화면이 된다. 잠깐 사이를 두고 세 번까지 다시 시도한다. */
    let cancelled = false;
    const verify = async (attempt = 1): Promise<void> => {
      try {
        await axios.post(`${API}/api/v1/client/payment-verify`, {
          consultation_id: consultationId,
          imp_uid: impUid,
          merchant_uid: merchantUid,
        }, { timeout: 120000 });
        if (cancelled) return;
        setStatus("ok");
        setTimeout(() => {
          navigate(`/report/${consultationId}`, { replace: true });
        }, 1200);
      } catch (err: any) {
        if (cancelled) return;
        if (attempt < 3) {
          setTimeout(() => { if (!cancelled) verify(attempt + 1); }, attempt * 2000);
          return;
        }
        setStatus("error");
        setErrMsg(
          (err.response?.data?.detail || t("pay.verifyError")) +
          t("pay.contactNote", { id: consultationId }),
        );
        setCid(consultationId);
      }
    };
    verify();
    return () => { cancelled = true; };
  }, [params, navigate, t]);

  return (
    <>
      <Global />
      <Wrap>
        <Card>
          {status === "loading" && (
            <>
              <Spinner />
              <Title>{t("pay.verifying")}</Title>
              <Desc style={{ whiteSpace: "pre-line" }}>{t("pay.verifyingDesc")}</Desc>
            </>
          )}
          {status === "ok" && (
            <>
              <Icon>✅</Icon>
              <Title>{t("pay.doneTitle")}</Title>
              <Desc style={{ whiteSpace: "pre-line" }}>{t("pay.doneDesc")}</Desc>
            </>
          )}
          {status === "error" && (
            <>
              <Icon>❌</Icon>
              <Title>{t("pay.errorTitle")}</Title>
              <Desc style={{ whiteSpace: "pre-line" }}>{errMsg}</Desc>
              {cid > 0 ? (
                <ErrBtn onClick={() => navigate(`/report/${cid}`)}>{t("pay.openReport")}</ErrBtn>
              ) : (
                <ErrBtn onClick={() => navigate("/client")}>{t("pay.retry")}</ErrBtn>
              )}
            </>
          )}
        </Card>
        <SiteFooter />
      </Wrap>
    </>
  );
}
